// ============================================================================
// AUTH.JS - Authentication Adapter
// Routes to demo auth or Firebase auth based on VITE_STORE_MODE
// ============================================================================

import { getStoreMode } from './store.adapter.js';

const IS_FIREBASE = getStoreMode() === 'firebase';

// ============================================================================
// DEMO AUTH (for local development without Firebase)
// ============================================================================

const DEMO_USERS = {
  'demo-gp': {
    id: 'demo-gp',
    email: 'gp@demo.local',
    name: 'Dr. Sarah Chen',
    role: 'gp_employee',
    tenantId: 'demo-clinic',
    status: 'active',
  },
  'demo-specialist': {
    id: 'demo-specialist',
    email: 'specialist@demo.local',
    name: 'Dr. James Wilson',
    role: 'specialist',
    tenantId: 'demo-clinic',
    status: 'active',
  },
  'demo-admin': {
    id: 'demo-admin',
    email: 'admin@demo.local',
    name: 'Tenant Admin',
    role: 'tenant_admin',
    tenantId: 'demo-clinic',
    status: 'active',
  },
  'demo-platform': {
    id: 'demo-platform',
    email: 'platform@demo.local',
    name: 'Platform Admin',
    role: 'platform_admin',
    tenantId: null,
    status: 'active',
  },
};

let demoCurrentUser = null;
let demoAuthCallbacks = [];

const demoSignIn = async (email, _password) => {
  // Find demo user by email or use first match
  const user = Object.values(DEMO_USERS).find(u => u.email === email) || DEMO_USERS['demo-gp'];
  demoCurrentUser = user;
  
  // Notify callbacks
  demoAuthCallbacks.forEach(cb => cb({ user, profile: user }));
  
  return { success: true, user, profile: user };
};

const demoSignUp = async (email, _password, displayName) => {
  // In demo mode, just create a GP user
  const user = {
    id: `demo-${Date.now()}`,
    email,
    name: displayName || email.split('@')[0],
    role: 'gp_employee',
    tenantId: 'demo-clinic',
    status: 'active',
  };
  demoCurrentUser = user;
  
  demoAuthCallbacks.forEach(cb => cb({ user, profile: user }));
  
  return { success: true, user, profile: user };
};

const demoLogOut = async () => {
  demoCurrentUser = null;
  demoAuthCallbacks.forEach(cb => cb({ user: null, profile: null }));
  return { success: true };
};

const demoOnAuthChange = (callback) => {
  demoAuthCallbacks.push(callback);
  
  // Immediately call with current state
  if (demoCurrentUser) {
    callback({ user: demoCurrentUser, profile: demoCurrentUser });
  } else {
    callback({ user: null, profile: null });
  }
  
  // Return unsubscribe function
  return () => {
    demoAuthCallbacks = demoAuthCallbacks.filter(cb => cb !== callback);
  };
};

const demoGetCurrentUser = () => demoCurrentUser;

// Demo-specific: quick login for demo users
export const demoQuickLogin = async (roleKey) => {
  const roleMap = {
    gp_employee: 'demo-gp',
    specialist: 'demo-specialist',
    tenant_admin: 'demo-admin',
    platform_admin: 'demo-platform',
  };
  
  const userId = roleMap[roleKey] || 'demo-gp';
  const user = DEMO_USERS[userId];
  demoCurrentUser = user;
  
  demoAuthCallbacks.forEach(cb => cb({ user, profile: user }));
  
  return { success: true, user, profile: user };
};

// ============================================================================
// FIREBASE AUTH (dynamic import)
// ============================================================================

let firebaseAuth = null;
let firebaseAuthPromise = null;

const getFirebaseAuth = async () => {
  if (firebaseAuth) return firebaseAuth;
  if (firebaseAuthPromise) return firebaseAuthPromise;
  
  firebaseAuthPromise = import('./auth.firebase.js').then((module) => {
    firebaseAuth = module;
    return firebaseAuth;
  });
  
  return firebaseAuthPromise;
};

// ============================================================================
// EXPORTED AUTH FUNCTIONS
// ============================================================================

/**
 * Sign in with email/password
 */
export const signIn = async (email, password) => {
  if (IS_FIREBASE) {
    const auth = await getFirebaseAuth();
    return auth.signIn(email, password);
  }
  return demoSignIn(email, password);
};

/**
 * Sign up with email/password
 */
export const signUp = async (email, password, displayName = null) => {
  if (IS_FIREBASE) {
    const auth = await getFirebaseAuth();
    return auth.signUp(email, password, displayName);
  }
  return demoSignUp(email, password, displayName);
};

/**
 * Sign out
 */
export const logOut = async () => {
  if (IS_FIREBASE) {
    const auth = await getFirebaseAuth();
    return auth.logOut();
  }
  return demoLogOut();
};

/**
 * Subscribe to auth state changes
 * Returns unsubscribe function
 */
export const onAuthChange = (callback) => {
  if (IS_FIREBASE) {
    // For Firebase, we need to handle the async import
    let unsubscribe = () => {};
    
    getFirebaseAuth().then((auth) => {
      unsubscribe = auth.onAuthChange(callback);
    });
    
    // Return a function that will unsubscribe once loaded
    return () => unsubscribe();
  }
  return demoOnAuthChange(callback);
};

/**
 * Get current user (sync for demo, may be null initially for Firebase)
 */
export const getCurrentUser = () => {
  if (IS_FIREBASE) {
    // This won't work well for Firebase due to async nature
    // Use onAuthChange instead
    return null;
  }
  return demoGetCurrentUser();
};

/**
 * Check if using Firebase auth
 */
export const isFirebaseAuth = () => IS_FIREBASE;

/**
 * Get demo users (only available in demo mode)
 */
export const getDemoUsers = () => IS_FIREBASE ? null : DEMO_USERS;

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  signIn,
  signUp,
  logOut,
  onAuthChange,
  getCurrentUser,
  isFirebaseAuth,
  getDemoUsers,
  demoQuickLogin,
};
